import merge from 'deepmerge';

import { SMARTY_EMBEDDED_KEY } from '@/consts/environmental';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { type FlowType } from '@/services/funnelService/consts/flowType';
import { type CheckoutEventType } from '@/types/checkout/types';

// TODO: Move URLs here as they are verified as working for multi-brand
export const createCommonApiUrls = <AppUrl extends string>(appUrl: AppUrl) =>
  ({
    accountCreation: {
      getProvider: (
        provider: string,
        redirectUrl: string,
        currentUrl: string,
      ) => {
        const redirectUrlParameter = encodeURIComponent(redirectUrl);
        const currentUrlParameter = encodeURIComponent(currentUrl);
        return `${appUrl}/app/register/social/${provider}?redirectUrl=${redirectUrlParameter}&currentUrl=${currentUrlParameter}`;
      },
    },
    applePay: {
      activateExpressCheckoutTrial: `${appUrl}/app/patient/payment/activate-express-checkout-trial`,
      activateExpressCheckoutSubscription: `${appUrl}/app/patient/payment/activate-express-checkout-subscription`,
    },
    auth: {
      isAuthenticated: `${appUrl}/api/auth/isAuthenticated`,
      csrf: `${appUrl}/api/auth/csrf`,
      store: `${appUrl}/api/auth/next`,
      login: `${appUrl}/api/login`,
    },
    cart: {
      getCart: `${appUrl}/api/ecommerce/cart`,
      updateCartItemQuantity: <CartId extends string, ItemId extends string>(
        cartId: CartId,
        itemId: ItemId,
      ) => `${appUrl}/api/ecommerce/cart/${cartId}/items/${itemId}` as const,
    },
    checkout: {
      cart: {
        getCheckoutData: `${appUrl}/api/checkout/cart`,
        processCheckout: `${appUrl}/api/checkout/cart`,
      },
      events: (eventType?: CheckoutEventType) => {
        const query = eventType ? `?event=${eventType}` : '';

        return `${appUrl}/api/checkout/events${query}`;
      },
    },
    completeMedicalHistoryUpdate: `${appUrl}/api/surveys/medical-history-update-complete`,
    consultations: {
      index: `${appUrl}/api/consultations`,
    },
    eligibility: {
      registration: `${appUrl}/api/eligibility/registration`,
      transitionOtcSignupUser: `${appUrl}/api/eligibility/registration/otc-transition`,
      evaluateRegistration: `${appUrl}/api/eligibility/evaluate/registration`,
      evaluateProduct: `${appUrl}/api/eligibility/evaluate/product`,
    },
    email: {
      sendVerification: `${appUrl}/api/patient/send-verification-email`,
      dev: {
        autoVerify: `${appUrl}/dev/auth/verify`,
      },
    },
    feature: {
      userContext: `${appUrl}/api/feature/user-context`,
    },
    funnels: {
      flow: {
        session: ({
          treatmentCategory,
          flowType,
        }: {
          treatmentCategory: TreatmentCategory;
          flowType: FlowType;
        }) =>
          `${appUrl}/api/flows/session?flowType=${flowType}&treatmentCategory=${treatmentCategory}` as const,
        show: <Flow extends string>(flow: Flow) =>
          `${appUrl}/api/flows/${flow}` as const,
        suggestStep: <Flow extends string>(flow: Flow) =>
          `${appUrl}/api/flows/${flow}/suggest` as const,
        canStart: <Flow extends string>(flow: Flow) =>
          `${appUrl}/api/flows/${flow}/canStart` as const,
      },
      survey: {
        show: <
          Provider extends string,
          Group extends string,
          Version extends number,
        >(
          provider: Provider,
          group: Group,
          version: Version,
        ) => `${appUrl}/api/surveys/${provider}/${group}/${version}` as const,
        store: <Provider extends string, SurveyId extends number>(
          provider: Provider,
          surveyId: SurveyId,
        ) => `/api/surveys/${provider}/${surveyId}/responses` as const,
        skinData: `${appUrl}/api/data-gateway/surveys/skin-data`,
        hairData: `${appUrl}/api/data-gateway/surveys/hair-data`,
      },
      signup: {
        complete: <TC extends TreatmentCategory, Type extends FlowType>(
          treatmentCategory: TC,
          flowType: Type,
        ) =>
          `${appUrl}/api/signup/complete/${treatmentCategory}/${flowType}` as const,
      },
    },
    guest: {
      middleware: `${appUrl}/api/guest/middleware`,
      cookieMiddleware: `${appUrl}/api/guest/middleware/cookies`,
    },
    liveConsultations: {
      timeSlots: {
        list: `${appUrl}/api/patient/live-consultations/available-time-slots`,
      },
      store: `${appUrl}/api/patient/live-consultations`,
      show: `${appUrl}/api/patient/live-consultations/scheduled`,
      cancel: <T extends number>(consultationId: T) =>
        `${appUrl}/api/patient/live-consultations/${consultationId}/cancel` as const,
      provider: `${appUrl}/api/patient/live-consultations/provider`,
    },
    me: {
      info: `${appUrl}/api/v3/me/info`,
    },
    offers: {
      getSessionData: `${appUrl}/api/offers/get-session-data`,
      show: <T extends string>(offerCategoryId: T) =>
        `${appUrl}/api/offers/${offerCategoryId}` as const,
      updateUserOffer: `${appUrl}/api/offers/update-user-offer`,
    },
    patient: {
      get: `${appUrl}/api/patient`,
      getBasic: `${appUrl}/api/shared/basic-patient`,
      updateLegalName: `${appUrl}/app/users/self/legal-name`,
    },
    payments: {
      storePaymentMethod: `${appUrl}/app/patient/payment/register-card`,
    },
    payPal: {
      activateExpressCheckoutTrial: `${appUrl}/app/patient/payment/activate-express-checkout-trial`,
      getBraintreeToken: `${appUrl}/api/patient/payment/braintree-token`,
    },
    phoneNumber: {
      get: `${appUrl}/api/phone-number`,
    },
    photos: {
      forFlows: `${appUrl}/app/photos/for-flows`,
      upload: `${appUrl}/app/photo/upload`,
      required: (treatmentCategory: string) =>
        `${appUrl}/app/photos/required/${treatmentCategory}` as const,
      delete: <T extends number>(photo: T) =>
        `${appUrl}/api/photos/${photo}` as const,
    },
    productSelectionAndCheckout: {
      activateTrial: `${appUrl}/app/patient/payment/activate-trial`,
      activateSubscription: `${appUrl}/app/patient/payment/activate-subscription`,
      getCheckoutData: <T extends TreatmentCategory>(treatmentCategory: T) =>
        `${appUrl}/api/patient/product-selection-and-checkout/checkout/${treatmentCategory}` as const,
      getProductSelectionData: <T extends TreatmentCategory>(
        treatmentCategory: T,
      ) =>
        `${appUrl}/api/patient/product-selection-and-checkout/product-selection/${treatmentCategory}` as const,
      getUpdatedProductSelectionData: <T extends TreatmentCategory>(
        treatmentCategory: T,
      ) =>
        `${appUrl}/api/patient/product-selection-and-checkout/get-updated-product-selection-data/${treatmentCategory}` as const,
      setPatientViewedAt: <T extends TreatmentCategory>(treatmentCategory: T) =>
        `${appUrl}/api/patient/product-selection-and-checkout/set-patient-viewed-at/${treatmentCategory}` as const,
      updateLegalName: `${appUrl}/app/users/self/legal-name`,
      updatePurchaseContext: `${appUrl}/api/checkout/update-purchase-context`,
      updateSelectedItems: `${appUrl}/app/patient/payment/subscription-items`,
      updateShippingAddress: `${appUrl}/app/users/self/address`,
    },
    promotion: {
      getPromotionForFlow: <Flow extends string>(flow: Flow) =>
        `${appUrl}/api/promotions/flows/${flow}` as const,
      store: `${appUrl}/api/promotion-claims`,
      createPromotionClaim: `${appUrl}/api/promotion-claims`,
      deletePromotionClaim: <PromotionCodeId extends number>(
        promotionCodeId: PromotionCodeId,
      ) => `${appUrl}/api/promotion-claims/${promotionCodeId}` as const,
      getWinbackPromotion: `${appUrl}/api/promotions/winback`,
    },
    referral: {
      get: `${appUrl}/api/patient/referral`,
    },
    referrerUtms: {
      store: `${appUrl}/api/capture-referrer`,
    },
    registration: {
      store: `${appUrl}/api/register`,
      completeBrandTransfer: `${appUrl}/api/register/brand-transfer/complete`,
      funnelBrandTransfer: `${appUrl}/api/patient/brand/transfer`,
    },
    resubscription: {
      data: {
        formulation: `${appUrl}/api/patient/resubscribe/data/formulation`,
        checkout: `${appUrl}/api/patient/resubscribe/data/checkout`,
      },
      previousResponses: `${appUrl}/api/patient/resubscribe/previous-responses`,
      resubscribe: `${appUrl}/api/patient/resubscribe`,
    },
    session: {
      flashMessages: `${appUrl}/api/flash-messages`,
    },
    signup: {
      info: `${appUrl}/api/signup`,
    },
    smarty: {
      getAutosuggestResults: (search: string) =>
        `https://us-autocomplete-pro.api.smarty.com/lookup?include_only_states=ALLSTATES&key=${SMARTY_EMBEDDED_KEY}&max_results=5&search=${encodeURIComponent(
          search,
        )}` as const,
    },
    sms: {
      confirmToken: `${appUrl}/api/phone-number/confirm`,
      doubleOptIn: `${appUrl}/iterable/preferences/double-opt-in`,
      preferences: `${appUrl}/api/patient/sms-preferences`,
      requiresVerification: `${appUrl}/api/phone-number/requires-verification`,
      sendConfirmation: `${appUrl}/api/phone-number/send-confirmation`,
      sendMessage: `${appUrl}/api/patient/sms-message`,
      unsubscribe: `${appUrl}/iterable/preferences/unsubscribe`,
      updatePhoneNumber: `${appUrl}/app/users/self/phone-number`,
    },
    subscriptions: {
      index: `${appUrl}/api/patient/subscriptions`,
      default: <T extends TreatmentCategory>(treatmentCategory: T) =>
        `${appUrl}/api/patient/subscriptions/default/${treatmentCategory}` as const,
    },
    upsells: {
      get: `${appUrl}/api/patient/upsells`,
      process: `${appUrl}/api/patient/upsells`,
    },
    url: `${appUrl}/api`,
    user: {
      healthHistory: `${appUrl}/api/patient/health-history`,
    },
    visitTypes: {
      show: (slug: string) => `${appUrl}/api/visit-types/${slug}` as const,
    },
    visits: {
      show: (uuid: string) => {
        return `${appUrl}/api/visits/${uuid}` as const;
      },
      verify: (uuid: string) => {
        return `${appUrl}/api/visits/${uuid}/verify` as const;
      },
      list: (visitTypeId: number) => {
        return `${appUrl}/api/visits?visit_type_id=${visitTypeId}` as const;
      },
      store: () => {
        return `${appUrl}/api/visits` as const;
      },
      eligibility: {
        store: (uuid: string) => {
          return `${appUrl}/api/visits/${uuid}/eligibility` as const;
        },
      },
      complete: (uuid: string) => {
        return `${appUrl}/api/visits/${uuid}/complete` as const;
      },
      editSessionBag: (uuid: string) => {
        return `${appUrl}/api/visits/${uuid}/edit-session-bag` as const;
      },
      estimate: {
        show: (uuid: string) => {
          return `${appUrl}/api/visits/${uuid}/estimate` as const;
        },
        subscription: {
          show: (uuid: string) => {
            return `${appUrl}/api/visits/${uuid}/estimate/subscription` as const;
          },
        },
      },
      recommendations: {
        show: (uuid: string) =>
          `${appUrl}/api/visits/${uuid}/recommendations` as const,
      },
      responses: {
        store: (uuid: string) =>
          `${appUrl}/api/visits/${uuid}/response` as const,
      },
      orders: {
        update: ({ uuid, orderId }: { uuid: string; orderId: number }) => {
          return `${appUrl}/api/visits/${uuid}/orders/${orderId}` as const;
        },
      },
      photos: {
        store: (uuid: string) => {
          return `${appUrl}/api/visits/${uuid}/photos` as const;
        },
        destroy: (uuid: string, photoId: number) => {
          return `${appUrl}/api/visits/${uuid}/photos/${photoId}` as const;
        },
      },
      questions: {
        store: () => {
          return `${appUrl}/api/protected/visits/questions` as const;
        },
      },
      register: {
        store: (uuid: string) => {
          return `${appUrl}/api/visits/${uuid}/register` as const;
        },
      },
      claim: (uuid: string) => {
        return `${appUrl}/api/visits/${uuid}/claim` as const;
      },
      shopifyResubCheckout: (uuid: string) => {
        return `${appUrl}/api/visits/${uuid}/shopify-resub-checkout`;
      },
      checkinSurvey: (uuid: string) => {
        return `${appUrl}/api/visits/${uuid}/checkin-survey` as const;
      },
    },
    logging: {
      datadog: `${appUrl}/api/datadog/log-event`,
    },
  }) as const;

export const createCurologyApiUrls = <AppUrl extends string>(appUrl: AppUrl) =>
  merge(createCommonApiUrls(appUrl), {
    guardian: {
      show: `${appUrl}/api/me/guardian`,
      store: `${appUrl}/api/me/guardian`,
    },
    me: {
      info: `${appUrl}/api/v3/me/info`,
    },
    registration: {
      store: `${appUrl}/api/register`,
      completeBrandTransfer: `${appUrl}/api/register/brand-transfer/complete`,
      funnelBrandTransfer: `${appUrl}/api/patient/brand/transfer`,
    },
    session: {
      flashMessages: `${appUrl}/api/flash-messages`,
    },
    stripe: {
      registerStripeCard: `${appUrl}/app/patient/payment/register-card`,
    },
    vip: {
      inviteCode: <Invite extends string>(invite: Invite) =>
        `${appUrl}/api/guest/invite/${invite}` as const,
    },
  } as const);

export const createAgencyApiUrls = createCommonApiUrls;
